import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { toastError, toastSuccess } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import {
	convertNumberToUSFormat,
	downloadBlobFIle,
	getFileType,
	getIconByFileType,
	openNewWindow
} from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

import {
	formatQuotationUsd,
	groupColorsByPrice,
	groupConsecutiveSizePrices
} from '@/features/order/utils/quotationPriceUtil';

const FACTORY_PRICE_APPROVAL_LABEL = {
	APPROVED: 'Approved',
	REJECTED: 'Rejected',
	PENDING: 'Approval Pending'
};

const Margin = () => {
	const [margins, setMargins] = useState([]);
	const [groupedData, setGroupedData] = useState([]);
	const { id } = useParams();
	const dispatch = useDispatch();
	const [newQtyCellId, setNewQtyCellId] = useState('');
	const [, setIsUpdateTerm] = useState(false);
	const prevTarget = useRef(null);

	const fetchMargins = useCallback(() => {
		dispatch(openLoader());
		ApiClient.GET(`${BASE_URL}/order/${id}/margin-summary`, '', true)
			.then((response) => {
				setMargins(response.data);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	}, [dispatch, id]);

	useEffect(() => {
		fetchMargins();
	}, [fetchMargins]);

	useEffect(() => {
		//group by supplierId. where supplierId and supplierName will be parent row and into parent there will key named rows which will have all the rows of that supplier
		let groupedData = margins?.projectedMarginDataList?.reduce((acc, item) => {
			if (!acc[item.supplierId]) {
				acc[item.supplierId] = {
					supplierName: item.supplierName,
					rows: []
				};
			}
			acc[item.supplierId].rows.push(item);
			return acc;
		}, {});
		setGroupedData(groupedData);
	}, [margins]);

	const handleDownload = () => {
		dispatch(openLoader());
		ApiClient.GET_FILE_DATA(`${BASE_URL}/order/${id}/report/margin-summary`, '', true)
			.then((response) => {
				downloadBlobFIle(response);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};

	const pendingFactoryPriceRows =
		margins?.projectedMarginDataList?.filter((item) => item?.factoryPriceApprovalStatus === 'PENDING') ||
		[];

	// Only the approver of a pending request can act on the factory price from the margin tab.
	const canApproveFactoryPrice = pendingFactoryPriceRows.some((item) => item?.canApproveFactoryPrice);

	// One request is created per style, so identical remarks/attachments are shown once for the whole batch.
	const factoryPriceApprovalNotes = Object.values(
		pendingFactoryPriceRows.reduce((acc, item) => {
			const attachments = item?.factoryPriceApprovalAttachments || [];
			if (!item?.factoryPriceApprovalRemarks && attachments.length === 0) return acc;

			const key = `${item?.factoryPriceApprovalRequestedBy}|${item?.factoryPriceApprovalRemarks}|${attachments
				.map((attachment) => attachment?.id)
				.join(',')}`;

			if (!acc[key]) {
				acc[key] = {
					key,
					requestedBy: item?.factoryPriceApprovalRequestedBy,
					requestedAt: item?.factoryPriceApprovalRequestedAt,
					remarks: item?.factoryPriceApprovalRemarks,
					attachments,
					referenceNumbers: []
				};
			}
			if (item?.referenceNumber && !acc[key].referenceNumbers.includes(item.referenceNumber))
				acc[key].referenceNumbers.push(item.referenceNumber);
			return acc;
		}, {})
	);

	const handleApproveOrRejectFactoryPrice = async (newStatus) => {
		const requestIdList = [
			...new Set(
				pendingFactoryPriceRows
					.map((item) => item?.factoryPriceApprovalRequestId)
					.filter((requestId) => requestId !== null && requestId !== undefined)
			)
		];
		if (requestIdList.length === 0) return;

		dispatch(openLoader());
		try {
			for (const requestId of requestIdList) {
				await ApiClient.PUT('approveRfqUpdateRequest', Number(requestId), `?newStatus=${newStatus}`);
			}
			toastSuccess(
				newStatus === 'APPROVED'
					? 'Factory price approval request approved'
					: 'Factory price approval request rejected'
			);
			dispatch(closeLoader());
			fetchMargins();
		} catch (error) {
			toastError(error?.response?.data?.message || 'Something went wrong! Please try again.');
			dispatch(closeLoader());
		}
	};

	const onChangeAddNewQty = (e, item) => {
		let { value } = e.target;
		//update setMargins(response.data); with new value of extraQuantityPercentage of every item of array with keeping other values same and other rows same
		let updatedMargins = margins?.projectedMarginDataList?.map((i) => {
			if (i.supplierId === item.supplierId) {
				return { ...i, extraQuantityPercentage: value };
			}
			return i;
		});
		setMargins({ ...margins, projectedMarginDataList: updatedMargins });
	};
	const getMergedRfqIdList = (supplierId) => {
		const mergedRfqIdList = margins?.projectedMarginDataList
			?.filter((item) => item.supplierId === supplierId)
			?.reduce((acc, item) => {
				if (item?.rfqId !== undefined && item?.rfqId !== null) {
					acc.push(item.rfqId);
				} else if (Array.isArray(item?.rfqIdList)) {
					acc = acc.concat(item.rfqIdList);
				}
				return acc;
			}, []);
		return mergedRfqIdList;
	};
	const onAddQty = async (item) => {
		// get rfqIdList from margins?.projectedMarginDataList of rfqIdList with item.supplierId and merge all rfqIdList into one array
		const supplierId = item.supplierId;
		const quoteIds = getMergedRfqIdList(supplierId);
		const body = {
			extraQuantityPercentage: item.extraQuantityPercentage,
			quoteIds
		};

		await ApiClient.POST('updateQuotes', body)
			.then(({ data }) => {
				dispatch(openLoader());
				if (data.success) {
					toastSuccess(data.message);
					dispatch(closeLoader());
				} else {
					toastError(data.message);
				}
			})
			.catch(({ response }) => {
				dispatch(closeLoader());
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const resetAll = () => {
		prevTarget.current = null;
		setNewQtyCellId('');
		setIsUpdateTerm(false);
	};

	const renderQuotationPriceDetails = (quotationType, designPrice, sizeRows, colorRows) => {
		if (quotationType === 'DESIGNWISE') {
			return <p className="regular-12 gray_dark_01 mb-0">{formatQuotationUsd(designPrice)}</p>;
		}
		if (quotationType === 'SIZEWISE') {
			const sizeGroups = groupConsecutiveSizePrices(sizeRows);
			return sizeGroups.length > 0 ? (
				sizeGroups.map((group, idx) => (
					<p className="regular-12 mb-1" key={`size-price-${idx}`}>
						<span className="gray_dark_02">{group.label}: </span>
						<span className="gray_dark_01">{formatQuotationUsd(group.price)}</span>
					</p>
				))
			) : (
				<p className="regular-12 gray_dark_02 mb-0">—</p>
			);
		}
		if (quotationType === 'COLORWISE') {
			const colorGroups = groupColorsByPrice(colorRows);
			return colorGroups.length > 0 ? (
				colorGroups.map((group, idx) => (
					<p className="regular-12 mb-1" key={`color-price-${idx}`}>
						<span className="gray_dark_02">{group.namesJoined}: </span>
						<span className="gray_dark_01">{formatQuotationUsd(group.price)}</span>
					</p>
				))
			) : (
				<p className="regular-12 gray_dark_02 mb-0">—</p>
			);
		}
		return <p className="regular-12 gray_dark_02 mb-0">—</p>;
	};

	const renderUnitPriceCell = (priceData = {}, type = 'buyer') => {
		const isBuyer = type === 'buyer';
		const quotationType = isBuyer
			? priceData?.buyerQuotationType
			: priceData?.factoryQuotationType || priceData?.buyerQuotationType;

		const designPrice = isBuyer
			? priceData?.designWiseBuyerPrice || priceData?.buyerPrice
			: priceData?.designWiseFactoryPrice || priceData?.factoryPrice;
		const sizeRows = isBuyer ? priceData?.sizeWiseBuyerPrices : priceData?.sizeWiseFactoryPrices;
		const colorRows = isBuyer ? priceData?.colorWiseBuyerPrices : priceData?.colorWiseFactoryPrices;

		return (
			<td className="price-column">
				{renderQuotationPriceDetails(quotationType, designPrice, sizeRows, colorRows)}
			</td>
		);
	};

	return (
		<div className="order-margin-table bg-white mt-3">
			<div className="title mb-3 d-flex align-items-center justify-content-between">
				<h3 className="semibold-16 mb-0">Order margin summary (Projected)</h3>
				{canApproveFactoryPrice && (
					<div className="d-flex align-items-center">
						<button
							className="button revise size32 mr-2"
							onClick={() => handleApproveOrRejectFactoryPrice('REJECTED')}
						>
							Reject
						</button>
						<button
							className="button approve size32"
							onClick={() => handleApproveOrRejectFactoryPrice('APPROVED')}
						>
							Approve
						</button>
					</div>
				)}
			</div>

			{factoryPriceApprovalNotes.length > 0 && (
				<div className="factory-price-approval-notes mb-3">
					{factoryPriceApprovalNotes.map((note) => (
						<div className="single-note" key={note.key}>
							<p className="regular-12 gray_dark_02 mb-1">
								Factory price approval request
								{note.requestedBy ? ` from ${note.requestedBy}` : ''}
								{note.requestedAt
									? ` · ${moment(note.requestedAt).format('DD MMM, YY')}`
									: ''}
								{note.referenceNumbers.length > 0
									? ` · ${note.referenceNumbers.join(', ')}`
									: ''}
							</p>
							{note.remarks && <p className="regular-14 gray_dark_01 mb-1">{note.remarks}</p>}
							{note.attachments.length > 0 && (
								<div className="d-flex flex-wrap align-items-center">
									{note.attachments.map((attachment) => (
										<div
											className="attachment d-flex align-items-center cursor-pointer mr-3"
											key={attachment?.id}
											onClick={() => openNewWindow(attachment?.docUrl)}
										>
											<img
												src={getIconByFileType(getFileType(attachment?.docUrl))}
												alt="attachment"
												width={16}
											/>
											<span className="regular-12 gray_dark_01 ml-1">
												{attachment?.originalName || attachment?.name}
											</span>
										</div>
									))}
								</div>
							)}
						</div>
					))}
				</div>
			)}

			<div className="margin-table scroll-x-label">
				<table className="table with-border height-sm">
					<thead>
						<tr>
							<th>Factory</th>
							{/* <th>Style({margins?.projectedMarginDataList?.length})</th> */}
							<th>Design({margins?.projectedMarginDataList?.length})</th>
							<th>PO Number</th>
							<th className="text-nowrap">{margins?.poItemIdentifier || 'SKU Number'}</th>
							<th>PO Received Date</th>
							<th className="text-nowrap">Buyer ETD</th>
							<th className="text-nowrap">Factory ETD</th>
							<th className="text-nowrap">PO Qty</th>
							<th>PO Unit Price</th>
							<th className="text-nowrap">PO value</th>
							<th>Qty Update(%)</th>
							<th>Factory Qty</th>
							<th>Factory Unit Price</th>
							<th>Factory cost</th>
							<th>Margin</th>
							<th>Margin(%)</th>
							<th className="text-nowrap">Approval status</th>
						</tr>
					</thead>
					<tbody>
						{groupedData &&
							Object.keys(groupedData).map(
								(key) =>
									groupedData[key]?.rows?.length > 0 &&
									groupedData[key]?.rows?.map((item, index) => (
										<tr
											key={`${key}-${item?.rfqId || index}`}
											className={
												item?.factoryPriceApprovalStatus === 'PENDING'
													? 'factory-price-approval-pending'
													: ''
											}
										>
											{index === 0 && (
												<td
													className="factory-column"
													rowSpan={groupedData[key]?.rows?.length}
												>
													{item?.supplierName}
												</td>
											)}
											<td className="text-nowrap">{item?.referenceNumber}</td>
											<td>
												<span className="text-nowrap">{item?.poNumber}</span>
											</td>
											<td>
												{item?.poItemIdentifierCode
													? item.poItemIdentifierCode
															.split(',')
															.map((code) => code.trim())
															.filter(Boolean)
															.join(', ')
													: ''}
											</td>
											<td className="text-nowrap">
												{item.poReceivedDate
													? moment(item.poReceivedDate).format('DD MMM, YY')
													: ''}
											</td>
											<td className="text-nowrap">
												{item.buyerDeliveryDate
													? moment(item.buyerDeliveryDate).format('DD MMM, YY')
													: ''}
											</td>
											<td className="text-nowrap">
												{item.factoryDeliveryDate
													? moment(item.factoryDeliveryDate).format('DD MMM, YY')
													: ''}
											</td>
											<td>{item?.poQuantity}</td>
											{renderUnitPriceCell(item?.priceData, 'buyer')}
											<td>${item?.poValue}</td>

											<td
												className="qty-update-column"
												onClick={() => {
													setNewQtyCellId(`newItem-${item.supplierId}`);
													setIsUpdateTerm(true);
												}}
											>
												{newQtyCellId === `newItem-${item.supplierId}` ? (
													<input
														type="number"
														autoComplete="off"
														id="term"
														value={item.extraQuantityPercentage}
														onChange={(e) => onChangeAddNewQty(e, item)}
														onFocus={(event) =>
															(prevTarget.current = event.target.value)
														}
														onBlur={() => {
															onAddQty(item);
															resetAll();
														}}
														onKeyDown={(e) => {
															if (e.key === 'Enter') {
																onAddQty(item, index);
																resetAll();
															}
														}}
													/>
												) : (
													<span className="gray_dark_02">
														{item.extraQuantityPercentage}
													</span>
												)}
											</td>
											<td>{item?.factoryQuantity}</td>
											{renderUnitPriceCell(item?.priceData, 'factory')}
											<td>${item?.factoryCost}</td>
											<td>
												{Math.sign(item?.marginValue) === -1 ? '-$' : '$'}
												{convertNumberToUSFormat(Math.abs(item?.marginValue))}
											</td>

											<td>
												{item?.marginPercentage == null ||
												isNaN(item.marginPercentage)
													? 0
													: item.marginPercentage.toFixed(2)}
												%
											</td>

											<td className="text-nowrap">
												{FACTORY_PRICE_APPROVAL_LABEL[
													item?.factoryPriceApprovalStatus
												] || 'Not requested'}
											</td>
										</tr>
									))
							)}

						<tr>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
							<td>
								<p className="gray_dark_01 weight-600">Total</p>
							</td>
							<td>{margins?.totalPoQuantity}</td>
							<td></td>
							<td>${margins?.totalPoValue}</td>
							<td></td>
							<td></td>
							<td>{margins?.totalFactoryQuantity}</td>
							<td>${margins?.totalFactoryCost}</td>
							<td>${margins?.totalMarginValue}</td>
							<td>
								{margins?.averageMarginPercentage != null
									? margins.averageMarginPercentage.toFixed(2)
									: '0.00'}
								%
							</td>
							<td></td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="table-footer-button">
				<div className="mt-3 text-right">
					<button className="button gray-outline size32 ml-2" onClick={handleDownload}>
						<img src={`${process.env.PUBLIC_URL}/icons/download-dark-20.svg`} alt="" /> Download
					</button>
				</div>
			</div>
		</div>
	);
};

export default Margin;
